import { useState } from "react";
import { useLocation, Link, useParams } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTheme } from "@/components/ui/theme-provider";
import { Sun, Moon, AlertCircle } from "lucide-react";
import logoBlack from "@assets/commentt_ai_logo_black.svg";
import { apiRequest } from "@/lib/queryClient";

const schema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirm: z.string().min(1, "Please confirm your password"),
}).refine(d => d.password === d.confirm, {
  message: "Passwords don't match",
  path: ["confirm"],
});

type ResetForm = z.infer<typeof schema>;

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const params = useParams<{ token: string }>();
  const token = params.token || "";
  const [serverError, setServerError] = useState(
    token ? "" : "Invalid or missing reset token. Please request a new reset link."
  );
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  const form = useForm<ResetForm>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  const mutation = useMutation({
    mutationFn: (data: ResetForm) =>
      apiRequest("POST", "/api/auth/reset-password", { token, password: data.password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setLocation("/");
    },
    onError: async (err: any) => {
      const body = await err.response?.json().catch(() => ({}));
      setServerError(body?.message || "Reset failed. Please request a new reset link.");
    },
  });

  function onSubmit(data: ResetForm) {
    setServerError("");
    mutation.mutate(data);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link href="/">
          <img src={logoBlack} alt="Commentt.ai" className="h-3.5 dark:invert" />
        </Link>
        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-1 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Set new password</h1>
            <p className="text-sm text-muted-foreground">Choose a strong password for your account</p>
          </div>

          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          {!serverError && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="At least 8 characters" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={mutation.isPending || !token}>
                  {mutation.isPending ? "Saving..." : "Set new password"}
                </Button>
              </form>
            </Form>
          )}

          {serverError && (
            <p className="text-center text-sm text-muted-foreground">
              <Link href="/forgot-password" className="text-foreground font-medium hover:underline">
                Request a new reset link
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
